import { useEffect, useRef, useState } from 'react';
import useGameStore, { MONEY_TRANSFER_TTL_MS } from '../store/gameStore';
import type { MoneyTransfer, Player } from '../types/gameTypes';
import { money } from '../lib/format';
import { useT } from '../i18n';

/** Smoothly ticks a displayed number towards its target over `duration` ms (800 ms default). */
const useTickingNumber = (target: number, duration = 800) => {
  const shownRef = useRef(target);
  const [shown, setShown] = useState(target);
  useEffect(() => {
    const from = shownRef.current;
    if (from === target) return;
    const start = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (target - from) * eased);
      shownRef.current = value;
      setShown(value);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return shown;
};

/** Re-renders only while animations are alive, so idle screens do no work. */
const useTransferClock = (active: boolean) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 180);
    return () => window.clearInterval(id);
  }, [active]);
};

const LedgerCard = ({ player, isActive, transfers }: { player: Player; isActive: boolean; transfers: MoneyTransfer[] }) => {
  const t = useT();
  const netWorthOf = useGameStore((s) => s.netWorthOf);
  const investmentValueOf = useGameStore((s) => s.investmentValueOf);
  const loanExposureOf = useGameStore((s) => s.loanExposureOf);
  const setInspectedPlayer = useGameStore((s) => s.setInspectedPlayer);
  const cash = useTickingNumber(player.cash, 800);
  const investments = investmentValueOf(player.id);
  const loans = loanExposureOf(player.id);
  const paid = transfers.filter((t) => t.fromId === player.id).reduce((sum, t) => sum + t.amount, 0);
  const got = transfers.filter((t) => t.toId === player.id).reduce((sum, t) => sum + t.amount, 0);
  const receiving = got > 0;
  const paying = !receiving && paid > 0;
  return (
    <button
      type="button"
      onClick={() => setInspectedPlayer(player.id)}
      data-player-card={player.id}
      className={`relative w-full cursor-pointer rounded-xl border p-2 text-left transition-all duration-300 hover:border-amber-400/50 hover:bg-slate-800/80 ${
        receiving
          ? 'ledger-glow-get border-emerald-400/80 bg-emerald-500/10'
          : paying
            ? 'ledger-glow-pay border-rose-400/80 bg-rose-500/10'
            : isActive
              ? 'border-amber-500/70 bg-amber-500/10'
              : 'border-slate-700/60 bg-slate-900/60'
      } ${player.isBankrupt ? 'opacity-40 grayscale' : ''}`}
      title={`${player.name}${player.isAI ? ' (bot)' : ''} · net worth ${money(netWorthOf(player.id))} · ${t('dossier.title')}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: player.color }} />
        <span className="min-w-0 flex-1 truncate text-[11px] font-bold text-slate-200">
          {player.name}
          {player.isAI ? ' 🤖' : ' 🧑'}
        </span>
        {isActive && !player.isBankrupt && <span className="shrink-0 text-[9px] font-black text-cyan-300">●</span>}
      </div>
      <p className={`mt-0.5 font-mono text-lg font-black leading-none ${player.cash < 0 ? 'text-rose-300' : 'text-slate-50'}`}>
        {money(cash)}
      </p>
      {(receiving || paying) && (
        <span className={`cash-float pointer-events-none absolute -top-3 right-1 text-xs font-black ${receiving ? 'text-emerald-300' : 'text-rose-300'}`}>
          {receiving ? `+${money(got)}` : `−${money(paid)}`}
        </span>
      )}
      {player.debt > 0 && <p className="text-[9px] font-bold text-rose-300/90">{t('common.debt')} {money(player.debt)}</p>}
      {/* Treasury tracking: structured-loan servicing + fund positions stay visible. */}
      {loans > 0 && (
        <p className="text-[9px] font-bold text-rose-300/90" title={`${money(loans)}`}>
          📉 {t('common.loans')} {money(loans)}
        </p>
      )}
      {investments > 0 && (
        <p className="text-[9px] font-bold text-emerald-300/90" title={`${money(investments)}`}>
          📈 {t('common.investments')} {money(investments)}
        </p>
      )}
    </button>
  );
};

/**
 * Permanent player/bot roster shown in the centre of the board: live cash in large mono
 * type, red glow while paying, emerald glow (with a floating +$X) while receiving.
 */
export const PlayerLedgerHud = () => {
  const players = useGameStore((s) => s.players);
  const currentPlayerIndex = useGameStore((s) => s.currentPlayerIndex);
  const moneyTransfers = useGameStore((s) => s.moneyTransfers);
  const fresh = moneyTransfers.filter((t) => Date.now() - t.ts < MONEY_TRANSFER_TTL_MS);
  useTransferClock(fresh.length > 0);
  const active = players[currentPlayerIndex];
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300/80">Live ledger</p>
      {players.map((player) => (
        <LedgerCard
          key={player.id}
          player={player}
          isActive={player.id === active?.id}
          transfers={fresh.filter((t) => t.fromId === player.id || t.toId === player.id)}
        />
      ))}
    </div>
  );
};

export default PlayerLedgerHud;
